(() => {
  'use strict';

  const CONFIG = window.MCOR_FITMENT_CONFIG || {};
  const STORAGE_KEY = 'mcor_fit_guide_vehicle_v1';
  const PARAMS = {
    year: 'fg_year', make: 'fg_make', model: 'fg_model', submodel: 'fg_submodel', body_style: 'fg_body',
    screen: 'fg_screen', amplifier: 'fg_amp', hvac: 'fg_hvac', key_type: 'fg_key',
    transmission: 'fg_trans', powertrain: 'fg_powertrain'
  };
  const text = value => String(value ?? '').trim();
  const token = value => text(value).toLowerCase().replace(/[^a-z0-9]+/g, '');
  const slug = value => text(value).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const unique = values => [...new Set(values.filter(Boolean))];
  const escapeHtml = value => text(value).replace(/[&<>'"]/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character]));
  const unknown = value => !text(value) || ['any', 'not-sure', 'all-trims-select-submodel'].includes(slug(value));
  const selectedVehicle = () => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); }
    catch (_error) { return null; }
  };
  const hasVehicle = vehicle => Boolean(vehicle?.year && vehicle?.make && vehicle?.model);
  const displayModel = value => {
    const model = text(value);
    const acronym = new Map([
      ['tl', 'TL'], ['tsx', 'TSX'], ['mdx', 'MDX'], ['rdx', 'RDX'], ['rl', 'RL'], ['ilx', 'ILX'], ['nsx', 'NSX'],
      ['cx-3', 'CX-3'], ['cx-30', 'CX-30'], ['cx-5', 'CX-5'], ['cx-50', 'CX-50'], ['cx-7', 'CX-7'], ['cx-9', 'CX-9'], ['cx-90', 'CX-90'],
      ['mx-5 miata', 'MX-5 Miata'], ['cr-v', 'CR-V'], ['hr-v', 'HR-V'], ['s2000', 'S2000'], ['fr-s', 'FR-S'], ['gr86', 'GR86'], ['brz', 'BRZ'],
    ]);
    return acronym.get(model.toLowerCase()) || model;
  };
  const vehicleLabel = vehicle => [vehicle?.year, vehicle?.make, displayModel(vehicle?.model)].filter(Boolean).join(' ');
  const productUrl = handle => `/products/${encodeURIComponent(handle)}`;
  const fitGuideUrl = (vehicle, anchor = '') => {
    const url = new URL(CONFIG.fitGuideUrl || '/pages/speaker-fit-guide', location.origin);
    Object.entries(PARAMS).forEach(([field, parameter]) => {
      if (vehicle?.[field]) url.searchParams.set(parameter, vehicle[field]);
    });
    url.hash = anchor;
    return `${url.pathname}${url.search}${url.hash}`;
  };

  async function compressedJson(url) {
    if (!url) return {};
    const response = await fetch(url, { credentials: 'same-origin', cache: 'force-cache' });
    if (!response.ok) throw new Error(`MCOR fitment data request failed: ${response.status}`);
    if (typeof DecompressionStream === 'undefined') throw new Error('This browser cannot read the compatibility data.');
    const compact = (await response.text()).replace(/[^A-Za-z0-9+/=]/g, '');
    const unpadded = compact.replace(/=+$/, '');
    const encoded = unpadded + '='.repeat((4 - (unpadded.length % 4)) % 4);
    const bytes = Uint8Array.from(atob(encoded), character => character.charCodeAt(0));
    const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'));
    return JSON.parse(await new Response(stream).text());
  }

  let catalogPromise;
  const makePromises = new Map();
  let metraPromise;
  let remotePromise;
  let currentPdpState = null;
  let cartAuditTimer = null;
  let cartAuditState = { missing: [] };
  const catalog = () => catalogPromise ||= compressedJson(CONFIG.catalogUrl).then(data => {
    data.byHandle = new Map((data.products || []).map(product => [product.h, product]));
    return data;
  });
  const assetUrl = name => {
    const url = new URL(CONFIG.catalogUrl, location.origin);
    url.pathname = url.pathname.replace(/[^/]+$/, name);
    
    return url.toString();
  };
  const makeRecords = make => {
    const key = slug(make);
    if (!makePromises.has(key)) makePromises.set(key, Promise.all([
      compressedJson(assetUrl(`mcor-fit-guide-apps-${key}.json.gz.b64`)),
      (metraPromise ||= compressedJson(CONFIG.metra2026Url))
    ]).then(([source, metra]) => [
      ...(source.records || []),
      ...(metra.records || []).filter(record => token(record?.vehicle?.make) === token(make))
    ]));
    return makePromises.get(key);
  };
  const remoteRecords = make => (remotePromise ||= compressedJson(CONFIG.remoteStartUrl))
    .then(data => (data.records || []).filter(record => token(record?.vehicle?.make) === token(make)));

  function modelMatches(record, vehicle) {
    const application = record?.vehicle || {};
    if (token(application.make) !== token(vehicle.make)) return false;
    const models = [application.model, ...(application.model_aliases || [])].map(token);
    if (!models.includes(token(vehicle.model))) return false;
    const year = Number(vehicle.year);
    return year >= Number(application.year_start) && year <= Number(application.year_end);
  }

  function selectedEquipment(vehicle) {
    return {
      screen: vehicle.screen,
      amplifier: vehicle.amplifier,
      hvac: vehicle.hvac,
      key_type: vehicle.key_type,
      transmission: vehicle.transmission,
      powertrain: vehicle.powertrain
    };
  }

  function comparable(required, chosen) {
    const left = slug(required), right = slug(chosen);
    if (!left || !right) return true;
    if (left.startsWith('non-') || right.startsWith('non-')) return left === right;
    return left === right || left.includes(right) || right.includes(left);
  }

  function qualifierMatch(record, vehicle) {
    const application = record?.vehicle || {};
    let conditional = record?.match_status === 'conditional';
    const chosenSubmodel = vehicle.submodel;
    const chosenBody = vehicle.body_style;
    const selectedSubmodelTokens = [chosenSubmodel, vehicle.trim].filter(value => !unknown(value)).map(slug);
    const requiredSubmodels = [application.trim, application.submodel].filter(Boolean).map(slug);
    const requiredBody = application.body_style;

    if (chosenBody && !unknown(chosenBody) && requiredBody && !comparable(requiredBody, chosenBody)) return { matches: false, explicit: true };
    if ((!chosenBody || unknown(chosenBody)) && requiredBody) conditional = true;
    if (selectedSubmodelTokens.length && (application.excluded_submodel_patterns || []).some(pattern => selectedSubmodelTokens.some(value => value.includes(slug(pattern))))) {
      return { matches: false, explicit: true };
    }
    if (requiredSubmodels.length && selectedSubmodelTokens.length && !requiredSubmodels.some(required => selectedSubmodelTokens.some(chosen => required.includes(chosen) || chosen.includes(required)))) {
      return { matches: false, explicit: true };
    }
    if (requiredSubmodels.length && !selectedSubmodelTokens.length) conditional = true;

    const equipment = application.factory_equipment || {};
    for (const [field, answer] of Object.entries(selectedEquipment(vehicle))) {
      const applicationValue = ['key_type', 'transmission', 'powertrain'].includes(field) ? application[field] : equipment[field];
      const required = Array.isArray(applicationValue) ? applicationValue.filter(Boolean) : applicationValue ? [applicationValue] : [];
      if (!required.length) continue;
      if (unknown(answer)) { conditional = true; continue; }
      if (!required.some(value => comparable(value, answer))) return { matches: false, explicit: true };
    }
    return { matches: true, conditional, explicit: false };
  }

  function normalizeSpeakerSize(value) {
    return text(value).toLowerCase().replace(/(?:inch|inches|["”])/g, '').trim()
      .replace(/^3\s*\/\s*4$/, '0.75').replace(/^1\s*3\/8$/, '1.38').replace(/^2\s*1\/2$/, '2.5')
      .replace(/^2\s*3\/4$/, '2.75').replace(/^3\s*1\/2$/, '3.5').replace(/^5\s*1\/4$/, '5.25')
      .replace(/^6\s*1\/2$/, '6.5').replace(/^6\s*3\/4$/, '6.75').replace(/\s*[x×]\s*/g, 'x')
      .replace(/\.0$/, '');
  }

  function speakerFit(product, vehicle, data) {
    if (product.k !== 'speaker' || !product.s?.length) return null;
    const key = [vehicle.year, token(vehicle.make), token(vehicle.model)].join('|');
    const allLocations = data.speaker_layouts?.[key] || [];
    const chosenBody = vehicle.body_style;
    const applicable = allLocations.filter(location => {
      if (location.b && !unknown(chosenBody) && !comparable(location.b, chosenBody)) return false;
      return true;
    });
    const sizes = new Set(product.s.map(normalizeSpeakerSize));
    const matches = applicable.filter(location => sizes.has(normalizeSpeakerSize(location.z)));
    if (!matches.length) return allLocations.length ? {
      status: 'speaker_mismatch',
      explanation: `This ${product.s.join(' / ')}-inch speaker does not match any published factory speaker opening for your ${vehicleLabel(vehicle)}${!unknown(chosenBody) ? ` ${chosenBody}` : ''}.`
    } : null;
    const locations = unique(matches.map(item => item.l)).join(', ');
    const parts = unique(matches.flatMap(item => item.p || []));
    return {
      status: 'size_match',
      explanation: `Fits the published ${locations || 'speaker'} opening in your ${vehicleLabel(vehicle)} by speaker size. Use the listed installation parts and confirm mounting depth before ordering.`,
      locations,
      requiredMpns: parts
    };
  }

  function requiredMpns(records, data) {
    const known = Object.keys(data.required_products || {}).sort((a, b) => b.length - a.length);
    return unique(records.flatMap(record => [
      ...(record?.components?.required_interface_or_module_mpns || []),
      ...(record?.components?.required_accessory_mpns || [])
    ]).map(value => text(value).toUpperCase())
      .filter(value => value && !value.includes('INCLUDED'))
      .map(value => known.find(mpn => token(value).includes(token(mpn))) || value));
  }

  async function resolve(handle, vehicle) {
    const data = await catalog();
    const product = data.byHandle.get(handle);
    if (!product?.vr) return { status: 'irrelevant', product };
    const speaker = speakerFit(product, vehicle, data);
    const records = [...await makeRecords(vehicle.make)];
    if (product.rr) records.push(...await remoteRecords(vehicle.make));
    let sameVehicle = records.filter(record => record?.product?.handle === handle && modelMatches(record, vehicle) && record.storefront_eligible !== false);
    const chosenBody = vehicle.body_style;
    if (chosenBody && !unknown(chosenBody) && sameVehicle.some(record => record?.vehicle?.body_style)) {
      // A current explicit body/cab application overrides older broad rows for
      // this same product and YMM. Keep only explicit rows so a mismatched cab
      // becomes a real conflict instead of falling through to a broad match.
      sameVehicle = sameVehicle.filter(record => record?.vehicle?.body_style);
    }
    const matched = [], conflicts = [];
    for (const record of sameVehicle) {
      const result = qualifierMatch(record, vehicle);
      if (result.matches) matched.push({ record, conditional: result.conditional });
      else if (result.explicit) conflicts.push(record);
    }
    if (matched.length) {
      const components = requiredMpns(matched.map(item => item.record), data);
      const conditional = matched.every(item => item.conditional);
      return {
        status: conditional ? 'conditional' : components.length ? 'required' : 'exact',
        product,
        records: matched.map(item => item.record),
        requiredMpns: components,
        // Required components remain visible purchasing guidance. Checkout
        // enforcement is reserved for the separately managed Kenwood
        // Direct-Fit complete-system builder in mcor-vehicle-context.js.
        enforceRequired: false,
        explanation: conditional
          ? `Listed for your ${vehicleLabel(vehicle)}, but factory equipment, trim, or the exact required-component path must be confirmed before ordering.${matched.some(item => item.record.source_conflict) ? ' Manufacturer sources disagree on this application; contact MCOR to verify the audio package and connector.' : ''}`
          : components.length
          ? `Verified for your ${vehicleLabel(vehicle)} when used with the required components shown below.`
          : `Verified application for your ${vehicleLabel(vehicle)}.`
      };
    }
    if (conflicts.length) return { status: 'excluded', product, records: conflicts, explanation: `This application conflicts with the factory equipment, body style, or submodel saved for your ${vehicleLabel(vehicle)}.` };
    if (speaker) return { ...speaker, product };
    if (product.u) return { status: 'universal', product, explanation: 'Universal product. Vehicle-specific installation parts and available space still need to be confirmed.' };
    return { status: 'no_verified', product, explanation: `No verified application for this product is currently published for your ${vehicleLabel(vehicle)}.` };
  }

  const labels = {
    exact: ['Verified fit', 'mcor-fitment--success'],
    required: ['Fits with required parts', 'mcor-fitment--success'],
    conditional: ['Check factory equipment', 'mcor-fitment--warning'],
    size_match: ['Fits your saved vehicle', 'mcor-fitment--success'],
    speaker_mismatch: ['Does not fit your saved vehicle', 'mcor-fitment--danger'],
    universal: ['Universal product', 'mcor-fitment--neutral'],
    excluded: ['Does not match saved vehicle', 'mcor-fitment--danger'],
    no_verified: ['No verified application', 'mcor-fitment--neutral']
  };

  function requiredMarkup(result, data) {
    if (!result.requiredMpns?.length) return '';
    const speakerParts = result.status === 'size_match';
    const items = result.requiredMpns.map(mpn => {
      const product = data.required_products?.[mpn];
      return product
        ? `<li><a href="${productUrl(product.h)}" target="_blank" rel="noopener">${escapeHtml(mpn)} — ${escapeHtml(product.t)}</a></li>`
        : speakerParts
        ? `<li>${escapeHtml(mpn)} <span>— verify the correct adapter or harness for the selected location</span></li>`
        : `<li>${escapeHtml(mpn)} <span>— required; contact MCOR if it is not listed</span></li>`;
    }).join('');
    const heading = speakerParts ? 'Published speaker installation parts' : 'Required for this application';
    return `<div class="mcor-fitment__requirements"><strong>${heading}</strong><ul>${items}</ul></div>`;
  }

  function addVehicleProperties(result, vehicle) {
    currentPdpState = { result, vehicle };
    document.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
      const values = {
        Vehicle: vehicleLabel(vehicle),
        '_mcor_fit_status': result.status,
        '_mcor_required_mpns': (result.requiredMpns || []).join('|')
      };
      for (const [name, value] of Object.entries(values)) {
        let input = form.querySelector(`input[name="properties[${CSS.escape(name)}]"]`);
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${name}]`;
          form.append(input);
        }
        input.value = value;
      }
    });
  }

  function syncCurrentForms() {
    if (currentPdpState) addVehicleProperties(currentPdpState.result, currentPdpState.vehicle);
  }

  function cartItemProperty(item, name) {
    const properties = item?.properties || {};
    return text(properties[name] ?? properties[`_${name}`]);
  }

  function checkoutTargets() {
    return [...document.querySelectorAll('button[name="checkout"], input[name="checkout"], a[href^="/checkout"], .cart__checkout-button')];
  }

  function removeCartWarning() {
    document.querySelectorAll('[data-mcor-cart-fitment-warning]').forEach(element => element.remove());
  }

  async function auditCart() {
    if (!document.querySelector('cart-drawer, cart-items, form[action*="/cart"]') && !checkoutTargets().length) return;
    try {
      const response = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) return;
      const cart = await response.json();
      const present = new Set((cart.items || []).flatMap(item => [item.sku, item.product_title, item.variant_title]).map(value => text(value).toUpperCase()).filter(Boolean));
      const required = unique((cart.items || []).flatMap(item => cartItemProperty(item, 'mcor_required_mpns').split('|')).map(value => text(value).toUpperCase()));
      const missing = required.filter(mpn => ![...present].some(value => value === mpn || value.includes(mpn)));
      // This audit is informational only. Installers and other shops often
      // purchase a single replacement/interface part intentionally. The
      // Kenwood Direct-Fit guard uses its own system manifest and remains the
      // only checkout-blocking compatibility workflow.
      cartAuditState = { missing };
      removeCartWarning();
      if (!missing.length) return;
      const data = await catalog();
      const links = missing.map(mpn => {
        const product = data.required_products?.[mpn];
        return product ? `<li><a href="${productUrl(product.h)}">${escapeHtml(mpn)} — ${escapeHtml(product.t)}</a></li>` : `<li>${escapeHtml(mpn)} — contact MCOR</li>`;
      }).join('');
      const notice = document.createElement('div');
      notice.dataset.mcorCartFitmentWarning = '';
      notice.className = 'mcor-cart-fitment-warning';
      notice.setAttribute('role', 'status');
      notice.innerHTML = `<strong>Review the related installation parts</strong><p>Your cart does not include every component referenced by the selected vehicle application. That may be intentional for a replacement or installer purchase; confirm what your installation needs before ordering.</p><ul>${links}</ul>`;
      const host = document.querySelector('.cart__footer, .drawer__footer, cart-drawer .drawer__inner, cart-items') || document.querySelector('main');
      host?.prepend(notice);
    } catch (error) {
      console.warn('[MCOR] Cart requirements could not be verified.', error);
    }
  }

  function scheduleCartAudit(delay = 120) {
    clearTimeout(cartAuditTimer);
    cartAuditTimer = setTimeout(auditCart, delay);
  }

  async function renderElement(element, vehicle) {
    const handle = element.dataset.productHandle;
    const context = element.dataset.mcorFitmentContext || 'card';
    if (!handle) return;
    if (!hasVehicle(vehicle)) {
      if (context === 'pdp') {
        element.hidden = false;
        element.className = 'mcor-fitment mcor-fitment--pdp mcor-fitment--neutral';
        element.innerHTML = `<div class="mcor-fitment__headline">Will this fit your vehicle?</div><p>Select your vehicle once to see verified applications and required installation parts.</p><a class="button button--secondary" href="${fitGuideUrl(null)}">Choose your vehicle</a>`;
      }
      return;
    }
    const result = await resolve(handle, vehicle);
    if (result.status === 'irrelevant') return;
    const [label, className] = labels[result.status] || labels.no_verified;
    element.hidden = false;
    element.className = `mcor-fitment mcor-fitment--${context} ${className}`;
    if (context === 'card') {
      element.innerHTML = `<button type="button" class="mcor-fitment__chip" aria-label="${escapeHtml(label)} for ${escapeHtml(vehicleLabel(vehicle))}" aria-expanded="false">${escapeHtml(label)}</button><span class="mcor-fitment__card-detail" hidden>${escapeHtml(result.explanation)}</span>`;
      element.title = result.explanation;
      return;
    }
    const data = await catalog();
    const details = [vehicle.submodel, vehicle.body_style].filter((value, index, all) => value && !unknown(value) && all.indexOf(value) === index).join(' • ');
    element.innerHTML = `
      <div class="mcor-fitment__header">
        <div><span class="mcor-fitment__eyebrow">Fitment for your vehicle</span><strong>${escapeHtml(vehicleLabel(vehicle))}</strong>${details ? `<small>${escapeHtml(details)}</small>` : ''}</div>
        <a href="${fitGuideUrl(vehicle)}">Change vehicle</a>
      </div>
      <div class="mcor-fitment__status"><span>${escapeHtml(label)}</span><p>${escapeHtml(result.explanation)}</p></div>
      ${requiredMarkup(result, data)}
      <div class="mcor-fitment__actions"><a href="${fitGuideUrl(vehicle, 'mfg-all-parts')}">See all verified products for this vehicle</a></div>`;
    addVehicleProperties(result, vehicle);
    setTimeout(syncCurrentForms, 250);
    setTimeout(syncCurrentForms, 1000);
  }

  async function renderAll() {
    const vehicle = selectedVehicle();
    const elements = [...document.querySelectorAll('[data-mcor-fitment]')];
    await Promise.all(elements.map(element => renderElement(element, vehicle).catch(error => {
      console.warn('[MCOR] Product fitment could not be resolved.', error);
      element.hidden = false;
      element.className = 'mcor-fitment mcor-fitment--neutral';
      element.innerHTML = `<strong>Fitment check temporarily unavailable</strong><p>We could not load the compatibility data. This is not a does-not-fit result.</p><a href="${fitGuideUrl(vehicle)}">Retry in the Vehicle Fit Guide</a>`;
    })));
  }

  const start = () => renderAll();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('submit', event => {
    if (event.target?.matches?.('form[action*="/cart/add"]')) syncCurrentForms();
  }, true);
  document.addEventListener('click', event => {
    const chip = event.target.closest?.('.mcor-fitment__chip');
    if (chip) {
      event.preventDefault();
      event.stopPropagation();
      const detail = chip.parentElement?.querySelector('.mcor-fitment__card-detail');
      if (detail) {
        const willOpen = detail.hidden;
        detail.hidden = !willOpen;
        chip.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      }
    }
  }, true);
  const observer = new MutationObserver(() => {
    syncCurrentForms();
    scheduleCartAudit();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
  scheduleCartAudit(500);
  window.addEventListener('storage', event => { if (event.key === STORAGE_KEY) renderAll(); });
  document.addEventListener('mcor:vehicle-changed', renderAll);
  document.addEventListener('cart:updated', () => scheduleCartAudit(50));
  window.addEventListener('pageshow', renderAll);
  window.MCORFitment = { resolve, renderAll, fitGuideUrl, selectedVehicle };
})();
